import React, { useState, Suspense, useMemo } from 'react'
import { ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { ErrorBoundary } from 'react-error-boundary'
import { ColorModeContext } from 'ducks/themeContext'
import useTheme from 'theme/useTheme'
import useColorMode from 'hooks/useColorMode'
import SnackProvider from 'components/Common/SnackProvider'
import ErrorFallback from 'components/Terminal/ErrorFallback'
import ProgressBar from 'components/Common/ProgressBar'

interface AppProvidersProps {
  children: React.ReactNode
}

const UIProviders: React.FC<{
  children: React.ReactNode
  mode: 'light' | 'dark'
  setMode: (mode: 'light' | 'dark') => void
}> = ({ children, mode, setMode }) => {
  const prefersDarkMode = useMemo(() => window.matchMedia('(prefers-color-scheme: dark)').matches, [])
  const colorMode = useColorMode(prefersDarkMode, setMode)
  const memoizedColorMode = useMemo(() => colorMode, [colorMode])
  const theme = useTheme(mode)
  const memoizedTheme = useMemo(() => theme, [theme])

  return (
    <ColorModeContext.Provider value={memoizedColorMode}>
      <ThemeProvider theme={memoizedTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

const InfraProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <SnackProvider>{children}</SnackProvider>
  </ErrorBoundary>
)

const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>((localStorage.getItem('mui-mode') as 'light' | 'dark') || 'dark')

  return (
    <Suspense fallback={<ProgressBar loadingCompleted={() => {}} />}>
      <UIProviders mode={mode} setMode={setMode}>
        <InfraProviders>{children}</InfraProviders>
      </UIProviders>
    </Suspense>
  )
}

export default AppProviders
